import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { describeLoadFailure, jsonOrThrow } from '../apiJson';
import { trackSerpResults } from '../analytics';
import { BETA_ENGAGEMENT, recordBetaEngagement } from '../betaToolbarEligibility';
import {
  trackApplyFilter,
  trackNoResults,
  trackOpenSignupModal,
  trackPaginate,
  trackSearch,
  trackViewSearchResults,
} from '../ga';
import { formatCount } from '../categoryLabels';
import { subcategorySlug } from '../categorySlug';
import { categoryBrowsePath, subcategoryBrowsePath } from '../categoryPaths';
import { shouldSubmitSearch } from '../searchSubmit';
import { SEARCH_RETAILER_FILTER_ENABLED } from '../constants/searchFilters';
import { addRecentSearch } from '../recentSearches';
import { useShare } from '../ShareContext';
import { HOME_PAGE_SHARE } from '../sharePayload';
import { HeaderMoreMenu } from './HeaderMoreMenu';
import { HeaderShareButton } from './HeaderShareButton';
import { LogoMark } from './LogoMark';
import { CategoryMenuDropdown } from './CategoryMenuDropdown';
import { SearchField } from './SearchField';
import { ProductCard } from './ProductCard';
import { ProductGridSkeleton } from './ProductCardSkeleton';
import {
  FilterViewToggle,
  SearchFiltersBar,
  parseScopedSearchQuery,
  resolveSearchBoxQuery,
} from './SearchFiltersBar';
import { GetAlertsButton } from './GetAlertsButton';
import { ShareToolbarButton } from './ShareToolbarButton';
import { SearchAlertSignupModal } from './SearchAlertSignupModal';
import { SearchAlertSignupPanel } from './SearchAlertSignupPanel';
import { SiteFooter } from './SiteFooter';
import {
  buildSearchAlertPath,
  buildSearchAlertPresentation,
  hasSaveableSearchScope,
} from '../searchAlertScope';
import { isScopedSearchParams } from '../searchScope';
import { PageHead } from './PageHead';
import { SubcategoryBrowseFacebookInline } from './SubcategoryBrowseFacebookInline';
import { useBrowseFacebookInlineLayout, shouldShowBrowseFacebookInlineAtIndex } from '../useSubcategoryFacebookInsertAfter';
import './SearchResultsPage.css';

const PRODUCTS_PER_PAGE = 30;
const LOAD_MORE_SKELETON_COUNT = 6;

/** @type {Record<string, number>} */
const SEARCH_FALLBACK_STEP_BY_MODE = {
  prefixBroad: 1,
  normalized: 2,
  normalizedBroad: 3,
  partialMatch: 4,
  dropSubcategoryScope: 5,
  phraseRewrite: 6,
  naturalLanguage: 7,
};

/**
 * @param {string | undefined} searchMode
 * @returns {{ step: number, label: string } | null}
 */
function getSearchFallbackStepInfo(searchMode) {
  if (typeof searchMode !== 'string' || searchMode === '') {
    return null;
  }

  const step = SEARCH_FALLBACK_STEP_BY_MODE[searchMode];
  if (typeof step !== 'number') {
    return null;
  }

  return { step, label: searchMode };
}

/**
 * @param {{
 *   query?: string,
 *   strictSearch?: boolean,
 *   searchFallback?: { searchMode?: string } | null,
 * }} params
 * @returns {number|null}
 */
function resolveSearchAnalyticsStep({ query, strictSearch, searchFallback }) {
  if ((query ?? '').trim() === '') {
    return null;
  }
  if (strictSearch) {
    return 0;
  }
  const stepInfo = getSearchFallbackStepInfo(searchFallback?.searchMode);
  if (stepInfo) {
    return stepInfo.step;
  }
  return 0;
}

function isSevereFallbackMode(searchMode) {
  return searchMode === 'phraseRewrite' || searchMode === 'naturalLanguage';
}

/** Bold emphasis for dynamic fallback copy (queries, counts, labels). */
function SearchFallbackEm({ children }) {
  return <strong className="results-page__fallback-em">{children}</strong>;
}

/**
 * @param {{
 *   searchMode?: string,
 *   originalQuery?: string,
 *   effectiveQuery?: string,
 *   estimatedTotal?: number,
 * }} offer
 * @returns {import('react').ReactNode}
 */
function getSearchFallbackOfferNotice(offer) {
  const original = (offer?.originalQuery ?? '').trim();
  const effective = (offer?.effectiveQuery ?? '').trim();
  const estimatedTotal = typeof offer?.estimatedTotal === 'number' ? offer.estimatedTotal : null;

  let message;
  switch (offer?.searchMode) {
    case 'phraseRewrite':
      message = effective
        ? (
          <>
            We could show results for &ldquo;
            <SearchFallbackEm>{effective}</SearchFallbackEm>
            &rdquo; instead of &ldquo;
            <SearchFallbackEm>{original}</SearchFallbackEm>
            &rdquo;.
          </>
        )
        : 'We could show results for a similar catalog phrase.';
      break;
    case 'naturalLanguage':
      message = 'We could show broader matches — not all search terms would match exactly.';
      break;
    default:
      message = 'We could show broader results for your search.';
  }

  if (estimatedTotal != null && estimatedTotal > 0) {
    return (
      <>
        {message}
        {' About '}
        <SearchFallbackEm>{estimatedTotal.toLocaleString()}</SearchFallbackEm>
        {' products found.'}
      </>
    );
  }

  return message;
}

/**
 * @param {string} userQuery
 * @param {string} subcategorySearch
 * @returns {string}
 */
function buildScopedSearchQuery(userQuery, subcategorySearch) {
  const user = userQuery.trim();
  const scope = subcategorySearch.trim();
  if (scope === '') {
    return user;
  }

  return user !== '' ? `${user} AND ${scope}` : scope;
}

/**
 * User keywords from a scoped query (subcategory AND suffix stripped when recognized).
 * @param {string} query
 * @param {import('./SearchFiltersBar').BrowseCategory[]} browseCategories
 * @param {string} shopCategorySlug
 * @returns {string[]}
 */
function parseUserSearchTerms(query, browseCategories, shopCategorySlug) {
  const { userQuery } = parseScopedSearchQuery(query, browseCategories, shopCategorySlug);
  if (userQuery.trim() === '') {
    return [];
  }

  return userQuery.trim().split(/\s+/).filter(Boolean);
}

/**
 * One user-query variation per removed term; full scoped query kept for navigation.
 * @param {string} originalQuery
 * @param {import('./SearchFiltersBar').BrowseCategory[]} browseCategories
 * @param {string} shopCategorySlug
 * @returns {Array<{ removedTerm: string, query: string, displayQuery: string }>}
 */
function buildPartialMatchQueryVariations(originalQuery, browseCategories, shopCategorySlug) {
  const { userQuery, subcategory } = parseScopedSearchQuery(
    originalQuery,
    browseCategories,
    shopCategorySlug,
  );
  const scopeSuffix = typeof subcategory?.search === 'string' ? subcategory.search.trim() : '';
  const terms = userQuery.trim().split(/\s+/).filter(Boolean);
  if (terms.length < 2) {
    return [];
  }

  const variations = [];
  for (const removedTerm of terms) {
    const remaining = terms.filter((term) => term !== removedTerm);
    if (remaining.length === 0) {
      continue;
    }

    const displayQuery = remaining.join(' ');
    variations.push({
      removedTerm,
      displayQuery,
      query: buildScopedSearchQuery(displayQuery, scopeSuffix),
    });
  }

  return variations;
}

/**
 * @param {{
 *   searchMode?: string,
 *   originalQuery?: string,
 *   effectiveQuery?: string,
 *   droppedTokens?: string[],
 *   token?: string,
 * } | null} searchFallback
 * @param {import('./SearchFiltersBar').BrowseCategory[]} browseCategories
 * @param {string} shopCategorySlug
 * @returns {import('react').ReactNode | null}
 */
function getSearchFallbackNotice(searchFallback, browseCategories, shopCategorySlug) {
  if (!searchFallback?.searchMode) {
    return null;
  }

  const original = (searchFallback.originalQuery ?? '').trim();
  const effective = (searchFallback.effectiveQuery ?? '').trim();
  const { userQuery: originalUser } = parseScopedSearchQuery(
    original,
    browseCategories,
    shopCategorySlug,
  );
  const queryLabel = originalUser.trim();

  switch (searchFallback.searchMode) {
    case 'prefixBroad':
      return queryLabel
        ? (
          <>
            No exact matches for &ldquo;
            <SearchFallbackEm>{queryLabel}</SearchFallbackEm>
            &rdquo; — showing similar terms instead.
          </>
        )
        : 'No exact matches for your search — showing similar terms instead.';
    case 'normalized':
    case 'normalizedBroad':
      return queryLabel
        ? (
          <>
            Showing results for a similar spelling of &ldquo;
            <SearchFallbackEm>{queryLabel}</SearchFallbackEm>
            &rdquo;.
          </>
        )
        : 'Showing results for a similar spelling of your search.';
    case 'partialMatch':
      return null;
    case 'dropSubcategoryScope':
      return (
        <>
          We couldn&apos;t find what you were looking for in this specific subcategory or category.
          {' So we\'re searching '}
          <SearchFallbackEm>everywhere</SearchFallbackEm>
          .
        </>
      );
    case 'phraseRewrite': {
      const { userQuery: effectiveUser } = parseScopedSearchQuery(
        effective,
        browseCategories,
        shopCategorySlug,
      );

      return effectiveUser
        ? (
          <>
            Showing results for &ldquo;
            <SearchFallbackEm>{effectiveUser}</SearchFallbackEm>
            &rdquo; instead of &ldquo;
            <SearchFallbackEm>{originalUser}</SearchFallbackEm>
            &rdquo;.
          </>
        )
        : 'Showing results for a similar catalog phrase.';
    }
    case 'naturalLanguage':
      return queryLabel
        ? (
          <>
            Showing the closest matches we could find for &ldquo;
            <SearchFallbackEm>{queryLabel}</SearchFallbackEm>
            &rdquo; — not all search terms matched exactly.
          </>
        )
        : 'Showing the closest matches we could find — not all search terms matched exactly.';
    default:
      return queryLabel
        ? (
          <>
            No exact matches for &ldquo;
            <SearchFallbackEm>{queryLabel}</SearchFallbackEm>
            &rdquo; — showing related results instead.
          </>
        )
        : 'No exact matches for your search — showing related results instead.';
  }
}

/**
 * @param {{
 *   searchFallback: {
 *     originalQuery?: string,
 *     effectiveQuery?: string,
 *     droppedTokens?: string[],
 *   },
 *   browseCategories: import('./SearchFiltersBar').BrowseCategory[],
 *   shopCategorySlug: string,
 *   onQuerySearch: (query: string) => void,
 * }} props
 */
function SearchPartialMatchNotice({
  searchFallback,
  browseCategories,
  shopCategorySlug,
  onQuerySearch,
}) {
  const original = (searchFallback.originalQuery ?? '').trim();
  const effective = (searchFallback.effectiveQuery ?? '').trim();
  const dropped = Array.isArray(searchFallback.droppedTokens)
    ? searchFallback.droppedTokens.filter(Boolean)
    : [];
  const { userQuery: originalUser, subcategory } = parseScopedSearchQuery(
    original,
    browseCategories,
    shopCategorySlug,
  );
  const { userQuery: effectiveUser } = parseScopedSearchQuery(
    effective,
    browseCategories,
    shopCategorySlug,
  );
  const scopeSuffix = typeof subcategory?.search === 'string' ? subcategory.search.trim() : '';
  const queryVariations = buildPartialMatchQueryVariations(
    original,
    browseCategories,
    shopCategorySlug,
  );
  const alternativeQueries = queryVariations.filter(
    (variation) => variation.displayQuery.trim() !== effectiveUser.trim(),
  );
  const variationForDroppedToken = (droppedTerm) => {
    const candidates = queryVariations.filter(
      (variation) => variation.displayQuery.trim() !== effectiveUser.trim()
        && parseUserSearchTerms(variation.query, browseCategories, shopCategorySlug).includes(droppedTerm),
    );

    if (candidates.length > 0) {
      candidates.sort(
        (a, b) => parseUserSearchTerms(b.query, browseCategories, shopCategorySlug).length
          - parseUserSearchTerms(a.query, browseCategories, shopCategorySlug).length,
      );
      return candidates[0].query;
    }

    const userTerms = parseUserSearchTerms(original, browseCategories, shopCategorySlug);
    const effectiveUserTerms = parseUserSearchTerms(effective, browseCategories, shopCategorySlug);
    const kept = userTerms.filter(
      (term) => term === droppedTerm || !effectiveUserTerms.includes(term),
    );

    return buildScopedSearchQuery(
      kept.length > 0 ? kept.join(' ') : droppedTerm,
      scopeSuffix,
    );
  };
  return (
    <>
      {effectiveUser ? (
        <>
          No exact matches for &ldquo;
          <SearchFallbackEm>{originalUser}</SearchFallbackEm>
          &rdquo; — showing results for &ldquo;
          <SearchFallbackEm>{effectiveUser}</SearchFallbackEm>
          &rdquo;
          {dropped.length > 0 && (
            <>
              {' '}
              (ignoring{' '}
              {dropped.map((term, index) => (
                <Fragment key={term}>
                  {index > 0 ? ', ' : ''}
                  <button
                    type="button"
                    className="results-page__fallback-term-link"
                    onClick={() => onQuerySearch(variationForDroppedToken(term))}
                  >
                    &ldquo;
                    <SearchFallbackEm>{term}</SearchFallbackEm>
                    &rdquo;
                  </button>
                </Fragment>
              ))}
              )
            </>
          )}
          .
        </>
      ) : (
        <>No exact matches for your search — showing broader results.</>
      )}
      {alternativeQueries.length > 0 && (
        <span className="results-page__fallback-term-options">
          {' '}
          Other similar searches:
          {' '}
          {alternativeQueries.map((variation, index) => (
            <Fragment key={variation.query}>
              {index > 0 && (
                <span className="results-page__fallback-term-sep" aria-hidden="true">
                  {' · '}
                </span>
              )}
              <button
                type="button"
                className="results-page__fallback-term-link"
                onClick={() => onQuerySearch(variation.query)}
              >
                &ldquo;
                <SearchFallbackEm>{variation.displayQuery}</SearchFallbackEm>
                &rdquo;
              </button>
            </Fragment>
          ))}
        </span>
      )}
    </>
  );
}

/**
 * @param {{
 *   embedded?: {
 *     shopCategorySlug: string,
 *     effectiveQuery: string,
 *     subcategory: { name: string, search: string, slug?: string },
 *     priceCollection?: { name: string, slug: string, priceMin: number, priceMax: number },
 *   } | null,
 * }} props
 */
export function SearchResultsPage({ embedded = null }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchFallback, setSearchFallback] = useState(null);
  const [searchFallbackOffer, setSearchFallbackOffer] = useState(null);
  const searchFallbackRef = useRef(null);
  const confirmedSevereFallbackRef = useRef(null);
  const [severeFallbackConfirmTick, setSevereFallbackConfirmTick] = useState(0);

  useEffect(() => {
    searchFallbackRef.current = searchFallback;
  }, [searchFallback]);

  const [browseCategories, setBrowseCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sortBy, setSortBy] = useState('featured');

  const [priceBounds, setPriceBounds] = useState(null);
  const [filterPriceMin, setFilterPriceMin] = useState(null);
  const [filterPriceMax, setFilterPriceMax] = useState(null);
  const [retailerNames, setRetailerNames] = useState([]);
  const [selectedRetailers, setSelectedRetailers] = useState(() => new Set());
  const [onlyInStock, setOnlyInStock] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [loadMoreRetry, setLoadMoreRetry] = useState(0);

  const loadMoreSentinelRef = useRef(null);

  const [searchAlertOpen, setSearchAlertOpen] = useState(false);
  const { openShareModal } = useShare();

  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = localStorage.getItem('shopsmt-product-view');
      return v === 'list' || v === 'grid' ? v : 'grid';
    } catch {
      return 'grid';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('shopsmt-product-view', viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  const activeWebsiteHost = embedded ? '' : (searchParams.get('website') ?? '');
  const categoryParam = embedded ? '' : (searchParams.get('category') ?? '');
  const productCategoryId = /^\d+$/.test(categoryParam) ? categoryParam : '';
  const shopCategorySlug = embedded
    ? embedded.shopCategorySlug.toLowerCase()
    : (searchParams.get('shop') ?? '').toLowerCase();
  const qFromUrl = embedded ? embedded.effectiveQuery : searchParams.get('q');
  const [embeddedSaleActive, setEmbeddedSaleActive] = useState(false);
  const saleActive = embedded
    ? embeddedSaleActive
    : searchParams.get('sale') === '1';

  const effectiveQuery = qFromUrl ?? '';

  const strictSearch = !embedded && searchParams.get('strict') === '1';

  useEffect(() => {
    setSearchFallback(null);
    setSearchFallbackOffer(null);
    confirmedSevereFallbackRef.current = null;
  }, [effectiveQuery, strictSearch]);

  const prevEffectiveQueryRef = useRef(effectiveQuery);

  const [draftQuery, setDraftQuery] = useState(() => {
    const pending = location.state?.searchBoxQuery;
    return typeof pending === 'string' ? pending : '';
  });
  const [scopePillDismissed, setScopePillDismissed] = useState(false);

  const scopedSearch = useMemo(
    () => parseScopedSearchQuery(
      effectiveQuery,
      browseCategories,
      shopCategorySlug,
    ),
    [effectiveQuery, browseCategories, shopCategorySlug],
  );

  useEffect(() => {
    setScopePillDismissed(false);
  }, [shopCategorySlug, effectiveQuery]);

  useEffect(() => {
    if (embedded) {
      setEmbeddedSaleActive(false);
    }
  }, [embedded, embedded?.shopCategorySlug, embedded?.effectiveQuery]);

  useEffect(() => {
    const next = resolveSearchBoxQuery(
      effectiveQuery,
      browseCategories,
      shopCategorySlug,
      scopedSearch.subcategory ?? embedded?.subcategory ?? null,
    );
    if (next !== undefined) {
      setDraftQuery(next);
    }
  }, [
    effectiveQuery,
    browseCategories,
    shopCategorySlug,
    scopedSearch.subcategory,
    embedded?.subcategory,
  ]);

  const retailerFilterKey = useMemo(
    () => (
      SEARCH_RETAILER_FILTER_ENABLED
        ? [...selectedRetailers].sort().join('\0')
        : ''
    ),
    [selectedRetailers],
  );

  const filterSessionKey = useMemo(
    () => [
      effectiveQuery,
      productCategoryId,
      activeWebsiteHost,
      shopCategorySlug,
      saleActive,
      onlyInStock,
      sortBy,
      filterPriceMin,
      filterPriceMax,
      retailerFilterKey,
    ].join('\0'),
    [
      effectiveQuery,
      productCategoryId,
      activeWebsiteHost,
      shopCategorySlug,
      saleActive,
      onlyInStock,
      sortBy,
      filterPriceMin,
      filterPriceMax,
      retailerFilterKey,
    ],
  );

  const scopeKey = useMemo(
    () => [
      effectiveQuery,
      productCategoryId,
      activeWebsiteHost,
      shopCategorySlug,
      saleActive,
    ].join('\0'),
    [
      effectiveQuery,
      productCategoryId,
      activeWebsiteHost,
      shopCategorySlug,
      saleActive,
    ],
  );

  const prevScopeKeyRef = useRef(null);
  const lastSerpImpressionKeyRef = useRef('');

  const prevFilterSessionKeyRef = useRef(filterSessionKey);
  const pageToFetch =
    prevFilterSessionKeyRef.current !== filterSessionKey ? 1 : page;

  useEffect(() => {
    prevFilterSessionKeyRef.current = filterSessionKey;
  }, [filterSessionKey]);

  useEffect(() => {
    if (pageToFetch !== page) {
      setPage(pageToFetch);
    }
  }, [pageToFetch, page]);

  const hasMore =
    !loading
    && loadError === null
    && products.length > 0
    && products.length < totalCount;

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMore || loadingMore || loadMoreError !== null) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) {
          return;
        }
        setPage((current) => {
          const nextPage = Math.floor(products.length / PRODUCTS_PER_PAGE) + 1;
          if (current >= nextPage) {
            return current;
          }
          return current + 1;
        });
      },
      { rootMargin: '400px 0px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMoreError, products.length, totalCount]);

  useEffect(() => {
    let cancelled = false;
    const isFirstPage = pageToFetch === 1;

    if (isFirstPage) {
      setLoading(true);
      setLoadError(null);
      setLoadMoreError(null);
    } else {
      setLoadingMore(true);
      setLoadMoreError(null);
    }

    const productsUrl = new URL('/api/products.php', window.location.origin);
    productsUrl.searchParams.set('q', effectiveQuery);
    if (activeWebsiteHost) {
      productsUrl.searchParams.set('website', activeWebsiteHost);
    } else {
      if (shopCategorySlug) {
        productsUrl.searchParams.set('shop', shopCategorySlug);
      }
      if (productCategoryId) {
        productsUrl.searchParams.set('category', productCategoryId);
      }
    }

    if (strictSearch) {
      productsUrl.searchParams.set('strict', '1');
    }

    const activeFallback = searchFallbackRef.current;
    const confirmedSevereToken = confirmedSevereFallbackRef.current;
    if (!strictSearch && effectiveQuery.trim() !== '') {
      if (typeof confirmedSevereToken === 'string' && confirmedSevereToken !== '') {
        productsUrl.searchParams.set('fb', confirmedSevereToken);
      } else if (
        typeof activeFallback?.token === 'string'
        && activeFallback.token !== ''
        && !isSevereFallbackMode(activeFallback.searchMode)
        && (
          (activeFallback.originalQuery ?? '').trim() === effectiveQuery.trim()
          || (
            activeFallback.browseScopeDropped === true
            && (activeFallback.effectiveQuery ?? '').trim() === effectiveQuery.trim()
          )
        )
      ) {
        productsUrl.searchParams.set('fb', activeFallback.token);
      }
    }

    if (saleActive) {
      productsUrl.searchParams.set('sale', '1');
    }

    if (onlyInStock) {
      productsUrl.searchParams.set('in_stock', '1');
    }

    if (sortBy !== 'featured') {
      productsUrl.searchParams.set('sort', sortBy);
    }

    if (filterPriceMin != null) {
      productsUrl.searchParams.set('price_min', String(filterPriceMin));
    }
    if (filterPriceMax != null) {
      productsUrl.searchParams.set('price_max', String(filterPriceMax));
    }

    if (SEARCH_RETAILER_FILTER_ENABLED) {
      for (const retailer of selectedRetailers) {
        productsUrl.searchParams.append('retailer', retailer);
      }
    }

    productsUrl.searchParams.set('page', String(pageToFetch));
    productsUrl.searchParams.set('per_page', String(PRODUCTS_PER_PAGE));

    const productsRequest = fetch(productsUrl.toString()).then((r) => jsonOrThrow(r));
    const sitesRequest = isFirstPage
      ? fetch('/api/browse-websites.php').then((r) => jsonOrThrow(r))
      : Promise.resolve(null);

    Promise.all([productsRequest, sitesRequest])
      .then(([prodData, siteData]) => {
        if (cancelled) {
          return;
        }
        const list = Array.isArray(prodData?.products) ? prodData.products : [];
        setProducts((prev) => (isFirstPage ? list : [...prev, ...list]));
        setTotalCount(
          typeof prodData?.total === 'number' && Number.isFinite(prodData.total)
            ? prodData.total
            : 0,
        );
        if (isFirstPage) {
          const nextSearchFallback = prodData?.searchFallback && typeof prodData.searchFallback === 'object'
            ? prodData.searchFallback
            : prodData?.broadFallback === true
              ? { applied: true, searchMode: 'prefixBroad' }
              : null;
          setSearchFallback(nextSearchFallback);
          setSearchFallbackOffer(
            prodData?.searchFallbackOffer && typeof prodData.searchFallbackOffer === 'object'
              ? prodData.searchFallbackOffer
              : null,
          );
          const nextMin =
            typeof prodData?.priceMin === 'number' ? prodData.priceMin : null;
          const nextMax =
            typeof prodData?.priceMax === 'number' ? prodData.priceMax : null;
          setPriceBounds(
            nextMin != null && nextMax != null
              ? { min: nextMin, max: nextMax }
              : null,
          );
          setRetailerNames(
            Array.isArray(prodData?.retailers) ? prodData.retailers : [],
          );
          if (filterPriceMin == null && nextMin != null && nextMax != null) {
            setFilterPriceMin(nextMin);
            setFilterPriceMax(nextMax);
          }
          if (siteData != null) {
            setBrowseCategories(
              Array.isArray(siteData?.categories) ? siteData.categories : [],
            );
          }

          const embeddedCollection = embedded?.priceCollection;
          if (embeddedCollection && isFirstPage) {
            setFilterPriceMin(Number(embeddedCollection.priceMin));
            setFilterPriceMax(Number(embeddedCollection.priceMax));
            setPriceBounds({
              min: Number(embeddedCollection.priceMin),
              max: Number(embeddedCollection.priceMax),
            });
          }

          const scopeChange =
            prevScopeKeyRef.current === null
            || prevScopeKeyRef.current !== scopeKey;
          prevScopeKeyRef.current = scopeKey;
          const resultsSource = shopCategorySlug
            ? 'category'
            : effectiveQuery.trim()
              ? 'search'
              : saleActive
                ? 'sale'
                : 'browse';
          const gaSearchFallback = prodData?.searchFallback && typeof prodData.searchFallback === 'object'
            ? prodData.searchFallback
            : prodData?.broadFallback === true
              ? { applied: true, searchMode: 'prefixBroad' }
              : null;
          const searchFallbackStep = resolveSearchAnalyticsStep({
            query: effectiveQuery,
            strictSearch,
            searchFallback: gaSearchFallback,
          });
          trackSerpResults(lastSerpImpressionKeyRef, {
            query: effectiveQuery,
            shopCategorySlug: shopCategorySlug || null,
            source: resultsSource,
            page: 1,
            items: list.map((p) => p.id),
            scopeChange,
            productCategoryId,
            websiteHost: activeWebsiteHost,
            saleActive,
            onlyInStock,
            sortBy,
            retailerFilterKey,
            filterPriceMin,
            filterPriceMax,
            domainMin: nextMin,
            domainMax: nextMax,
            searchFallbackStep,
          });

          const gaTotal =
            typeof prodData?.total === 'number' && Number.isFinite(prodData.total)
              ? prodData.total
              : 0;
          const gaHasActiveFilters =
            onlyInStock
            || retailerFilterKey !== ''
            || (
              nextMin != null
              && nextMax != null
              && filterPriceMin != null
              && filterPriceMax != null
              && (filterPriceMin > nextMin || filterPriceMax < nextMax)
            );
          const gaParams = {
            totalCount: gaTotal,
            resultsSource,
            pageNumber: 1,
            hasActiveFilters: gaHasActiveFilters,
            broadFallback: prodData?.broadFallback === true || gaSearchFallback?.applied === true,
            searchFallbackStep,
            shopCategorySlug: shopCategorySlug || '',
          };
          if (gaTotal > 0) {
            trackViewSearchResults(gaParams);
          } else {
            trackNoResults(gaParams);
            recordBetaEngagement(BETA_ENGAGEMENT.NO_RESULTS);
          }
        }
      })
      .catch((e) => {
        if (!cancelled) {
          if (isFirstPage) {
            setLoadError(describeLoadFailure(e));
          } else {
            setLoadMoreError(describeLoadFailure(e));
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          if (isFirstPage) {
            setLoading(false);
          } else {
            setLoadingMore(false);
          }
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    effectiveQuery,
    productCategoryId,
    activeWebsiteHost,
    shopCategorySlug,
    saleActive,
    onlyInStock,
    sortBy,
    filterPriceMin,
    filterPriceMax,
    retailerFilterKey,
    pageToFetch,
    scopeKey,
    strictSearch,
    severeFallbackConfirmTick,
    loadMoreRetry,
  ]);

  function retryLoadMore() {
    setLoadMoreError(null);
    setLoadMoreRetry((n) => n + 1);
  }

  function confirmSevereFallback() {
    const token = searchFallbackOffer?.token;
    if (typeof token !== 'string' || token === '') {
      return;
    }
    confirmedSevereFallbackRef.current = token;
    setSearchFallbackOffer(null);
    setPage(1);
    setProducts([]);
    setSevereFallbackConfirmTick((n) => n + 1);
  }

  const appliedFallbackStep = getSearchFallbackStepInfo(searchFallback?.searchMode)?.step ?? null;

  const searchAnalyticsStep = useMemo(
    () => resolveSearchAnalyticsStep({
      query: effectiveQuery,
      strictSearch,
      searchFallback,
    }),
    [effectiveQuery, strictSearch, searchFallback],
  );

  const activeCategoryName = useMemo(
    () => browseCategories.find((c) => c.slug === shopCategorySlug)?.name,
    [browseCategories, shopCategorySlug],
  );

  const activePriceCollection = useMemo(
    () =>
      browseCategories.find(
        (c) => c.slug === shopCategorySlug && c.priceCollection === true,
      ) ?? embedded?.priceCollection ?? null,
    [browseCategories, shopCategorySlug, embedded?.priceCollection],
  );

  useEffect(() => {
    setSelectedRetailers(new Set());
    setOnlyInStock(false);
    setFilterPriceMin(null);
    setFilterPriceMax(null);
    setPriceBounds(null);
    setSortBy('featured');
  }, [productCategoryId, activeWebsiteHost, effectiveQuery]);

  useEffect(() => {
    setSelectedRetailers(new Set());
    setOnlyInStock(false);
  }, [shopCategorySlug]);

  useEffect(() => {
    if (shopCategorySlug === '') {
      setFilterPriceMin(null);
      setFilterPriceMax(null);
      setPriceBounds(null);
      return;
    }
    if (activePriceCollection) {
      setFilterPriceMin(Number(activePriceCollection.priceMin));
      setFilterPriceMax(Number(activePriceCollection.priceMax));
      setPriceBounds({
        min: Number(activePriceCollection.priceMin),
        max: Number(activePriceCollection.priceMax),
      });
      return;
    }
    setFilterPriceMin(null);
    setFilterPriceMax(null);
    setPriceBounds(null);
  }, [
    shopCategorySlug,
    activePriceCollection?.slug,
    activePriceCollection?.priceMin,
    activePriceCollection?.priceMax,
  ]);

  const domainMin = activePriceCollection
    ? Number(activePriceCollection.priceMin)
    : (priceBounds?.min ?? 0);
  const domainMax = activePriceCollection
    ? Number(activePriceCollection.priceMax)
    : (priceBounds?.max ?? 0);
  const filterMin = filterPriceMin ?? domainMin;
  const filterMax = filterPriceMax ?? domainMax;

  const visibleRetailerNames = useMemo(
    () => [...retailerNames].sort((a, b) => a.localeCompare(b)),
    [retailerNames],
  );

  const outboundCampaign = useMemo(() => {
    if (shopCategorySlug) {
      return 'category';
    }
    if (effectiveQuery.trim()) {
      return 'search';
    }
    if (saleActive) {
      return 'sale';
    }
    return 'browse';
  }, [shopCategorySlug, effectiveQuery, saleActive]);

  useBrowseFacebookInlineLayout();
  const embeddedBrowseScopeLabel = useMemo(() => {
    const subName =
      typeof embedded?.subcategory?.name === 'string'
        ? embedded.subcategory.name.trim()
        : '';
    if (subName !== '') {
      return subName;
    }
    const collectionName =
      typeof embedded?.priceCollection?.name === 'string'
        ? embedded.priceCollection.name.trim()
        : '';
    return collectionName;
  }, [embedded]);
  const browseFacebookLocation = embedded?.priceCollection
    ? 'price_collection_browse'
    : 'subcategory_browse';
  const showBrowseFacebookInline =
    (embedded?.subcategory != null || embedded?.priceCollection != null)
    && viewMode === 'grid'
    && embeddedBrowseScopeLabel !== '';
  const searchScopePill = useMemo(() => {
    if (scopePillDismissed || !shopCategorySlug) {
      return null;
    }
    if (scopedSearch.subcategory) {
      return { label: scopedSearch.subcategory.name };
    }
    if (activeCategoryName) {
      return { label: activeCategoryName };
    }
    return null;
  }, [
    scopePillDismissed,
    scopedSearch.subcategory,
    shopCategorySlug,
    activeCategoryName,
  ]);

  const hasActiveFilters =
    onlyInStock
    || retailerFilterKey !== ''
    || (
      priceBounds != null
      && filterPriceMin != null
      && filterPriceMax != null
      && (filterPriceMin > priceBounds.min || filterPriceMax < priceBounds.max)
    );

  const searchAlertScopeInput = useMemo(
    () => ({
      effectiveQuery,
      shopCategorySlug,
      saleActive,
      activeWebsiteHost,
      productCategoryId,
      onlyInStock,
      filterPriceMin,
      filterPriceMax,
      priceBounds,
      selectedRetailers,
      browseCategories,
    }),
    [
      effectiveQuery,
      shopCategorySlug,
      saleActive,
      activeWebsiteHost,
      productCategoryId,
      onlyInStock,
      filterPriceMin,
      filterPriceMax,
      priceBounds,
      selectedRetailers,
      browseCategories,
    ],
  );

  const showSearchAlerts = useMemo(
    () => hasSaveableSearchScope(searchAlertScopeInput),
    [searchAlertScopeInput],
  );

  const searchAlertPayload = useMemo(() => {
    const { scopeLabel, chips } = buildSearchAlertPresentation(searchAlertScopeInput);
    return {
      searchPath: buildSearchAlertPath(searchAlertScopeInput),
      scopeLabel,
      scopeChips: chips,
    };
  }, [searchAlertScopeInput]);

  function scrollResultsToTop() {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    window.scrollTo({ top: 0, left: 0 });
  }

  useEffect(() => {
    if (prevEffectiveQueryRef.current === effectiveQuery) {
      return;
    }
    prevEffectiveQueryRef.current = effectiveQuery;
    scrollResultsToTop();
  }, [effectiveQuery]);

  function dismissCategoryScopePill() {
    setScopePillDismissed(true);
  }

  const searchSubmitScope = useMemo(
    () => ({
      shopSlug: scopePillDismissed ? '' : shopCategorySlug,
      subcategory: scopePillDismissed ? null : scopedSearch.subcategory,
    }),
    [scopePillDismissed, shopCategorySlug, scopedSearch.subcategory],
  );

  /** Clear SERP filters when the user runs a new search (including the same query). */
  function resetFiltersForRequery(urlParams = null) {
    setSelectedRetailers(new Set());
    setOnlyInStock(false);
    setFilterPriceMin(null);
    setFilterPriceMax(null);
    setPriceBounds(null);
    setSortBy('featured');
    setPage(1);
    if (embedded) {
      setEmbeddedSaleActive(false);
    } else if (urlParams) {
      urlParams.delete('sale');
      urlParams.delete('strict');
      urlParams.delete('fb');
      urlParams.delete('page');
    }
    setSearchFallback(null);
    setSearchFallbackOffer(null);
    confirmedSevereFallbackRef.current = null;
  }

  function applySearch(query) {
    const trimmed = query.trim();
    setDraftQuery(trimmed);
    const next = new URLSearchParams(searchParams);

    if (scopePillDismissed) {
      if (!shouldSubmitSearch(trimmed, {})) {
        return;
      }
      next.delete('shop');
      next.delete('website');
      next.delete('category');
      next.set('q', trimmed);
      resetFiltersForRequery(next);
      setSearchParams(next);
      setScopePillDismissed(false);
      scrollResultsToTop();
      return;
    }

    if (!shouldSubmitSearch(trimmed, searchSubmitScope)) {
      return;
    }

    if (scopedSearch.subcategory) {
      next.delete('website');
      next.delete('category');
      next.set('shop', shopCategorySlug);
      next.set(
        'q',
        trimmed
          ? `${trimmed} AND ${scopedSearch.subcategory.search}`
          : scopedSearch.subcategory.search,
      );
    } else if (shopCategorySlug) {
      next.delete('website');
      next.delete('category');
      next.set('shop', shopCategorySlug);
      next.set('q', trimmed);
    } else {
      next.set('q', trimmed);
    }

    resetFiltersForRequery(next);
    setSearchParams(next);
    scrollResultsToTop();
  }

  function applyFallbackQuerySearch(query) {
    const trimmed = query.trim();
    if (trimmed === '') {
      return;
    }

    trackSearch(trimmed, 'fallback_term_link');
    addRecentSearch(trimmed);

    if (embedded) {
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.set('q', trimmed);
    resetFiltersForRequery(next);
    setSearchParams(next);
    scrollResultsToTop();
  }

  const prevPageRef = useRef(pageToFetch);

  useEffect(() => {
    if (pageToFetch > prevPageRef.current && pageToFetch > 1) {
      trackPaginate('next', pageToFetch);
    }
    prevPageRef.current = pageToFetch;
  }, [pageToFetch]);

  function setSaleFilter(active) {
    trackApplyFilter('sale', active ? 'on' : 'off');
    if (embedded) {
      setEmbeddedSaleActive(active);
      scrollResultsToTop();
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (active) {
      next.set('sale', '1');
    } else {
      next.delete('sale');
    }
    setSearchParams(next);
  }

  function setShopInUrl(slug) {
    if (slug) {
      navigate(categoryBrowsePath(slug));
      return;
    }
    if (embedded) {
      navigate('/');
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('website');
    next.delete('category');
    next.delete('shop');
    if (isScopedSearchParams(next)) {
      setSearchParams(next);
    } else {
      navigate('/');
    }
  }

  function setSubcategoryInUrl(shopSlug, search) {
    if (embedded) {
      const cat = browseCategories.find((c) => c.slug === shopSlug);
      const sub = (cat?.subcategories ?? []).find((s) => s.search === search);
      if (sub) {
        navigate(subcategoryBrowsePath(shopSlug, subcategorySlug(sub)));
      } else {
        const params = new URLSearchParams({ shop: shopSlug, q: search });
        navigate(`/search?${params.toString()}`);
      }
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('website');
    next.delete('category');
    next.set('shop', shopSlug);
    next.set('q', search);
    setSearchParams(next);
  }

  function setShopFilterInUrl(slug) {
    trackApplyFilter('category', slug || 'clear');
    const userQuery = parseScopedSearchQuery(
      effectiveQuery,
      browseCategories,
      shopCategorySlug,
    ).userQuery.trim();

    if (embedded) {
      const params = new URLSearchParams();
      if (slug) {
        params.set('shop', slug);
      }
      if (userQuery) {
        params.set('q', userQuery);
      }
      navigate(params.toString() ? `/search?${params.toString()}` : '/search');
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete('website');
    next.delete('category');
    if (slug) {
      next.set('shop', slug);
    } else {
      next.delete('shop');
    }
    if (userQuery) {
      next.set('q', userQuery);
    } else {
      next.delete('q');
    }
    if (isScopedSearchParams(next)) {
      setSearchParams(next);
    } else {
      navigate('/');
    }
  }

  function setSubcategoryFilterInUrl(shopSlug, search) {
    trackApplyFilter('category', 'subcategory');
    const userQuery = parseScopedSearchQuery(
      effectiveQuery,
      browseCategories,
      shopCategorySlug,
    ).userQuery.trim();
    const subSearch = typeof search === 'string' ? search.trim() : '';
    let combinedQuery = subSearch;
    if (userQuery && subSearch) {
      combinedQuery = `${userQuery} AND ${subSearch}`;
    } else if (userQuery) {
      combinedQuery = userQuery;
    }

    if (embedded) {
      const params = new URLSearchParams({ shop: shopSlug });
      if (combinedQuery) {
        params.set('q', combinedQuery);
      }
      navigate(`/search?${params.toString()}`);
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete('website');
    next.delete('category');
    next.set('shop', shopSlug);
    if (combinedQuery) {
      next.set('q', combinedQuery);
    } else {
      next.delete('q');
    }
    setSearchParams(next);
  }

  function toggleRetailer(name) {
    setSelectedRetailers((prev) => {
      const next = new Set(prev);
      const adding = !next.has(name);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      trackApplyFilter('retailer', adding ? 'add' : 'remove');
      return next;
    });
  }

  function handleOnlyInStockChange(active) {
    trackApplyFilter('in_stock', active ? 'on' : 'off');
    setOnlyInStock(active);
  }

  function handleViewModeChange(mode) {
    trackApplyFilter('view_mode', mode);
    setViewMode(mode);
  }

  function handleSortChange(value) {
    trackApplyFilter('sort', value);
    setSortBy(value);
  }

  function handleFilterMinChange(value) {
    trackApplyFilter('price', 'min');
    setFilterPriceMin(value);
  }

  function handleFilterMaxChange(value) {
    trackApplyFilter('price', 'max');
    setFilterPriceMax(value);
  }

  function openSearchAlertModal() {
    trackOpenSignupModal('search_alert', shopCategorySlug ? 'category' : 'search');
    setSearchAlertOpen(true);
  }

  function openSearchShareModal() {
    if (hasSaveableSearchScope(searchAlertScopeInput)) {
      openShareModal({
        path: searchAlertPayload.searchPath,
        scopeLabel: searchAlertPayload.scopeLabel,
        location: 'search_toolbar',
      });
      return;
    }
    openShareModal({ location: 'search_toolbar' });
  }

  /** @param {'empty' | 'end'} panelVariant */
  function openPanelShareModal(panelVariant) {
    openShareModal({
      path: '/',
      location: panelVariant === 'end' ? 'search_panel_end' : 'search_panel_empty',
    });
  }

  function openBrowseInlineShareModal() {
    openShareModal({
      ...HOME_PAGE_SHARE,
      location: embedded?.priceCollection
        ? 'price_collection_browse_inline'
        : 'subcategory_browse_inline',
    });
  }

  const isShopBrowseOnly =
    !embedded
    && shopCategorySlug !== ''
    && effectiveQuery.trim() === ''
    && !saleActive
    && activeWebsiteHost === ''
    && productCategoryId === '';

  if (!embedded && !isScopedSearchParams(searchParams)) {
    return <Navigate to="/" replace />;
  }

  if (isShopBrowseOnly) {
    return <Navigate to={categoryBrowsePath(shopCategorySlug)} replace />;
  }

  const resultsMain = (
      <div className="results-page__main">
        {loadError === null &&
          (totalCount > 0 || browseCategories.length > 0 || saleActive || priceBounds != null) && (
          <div className="results-page__filters results-page__content-width">
            <SearchFiltersBar
              browseCategories={browseCategories}
              activeShopCategorySlug={shopCategorySlug}
              activeQuery={effectiveQuery}
              onShopCategoryChange={setShopInUrl}
              onSubcategoryChange={setSubcategoryInUrl}
              onShopCategoryFilterChange={setShopFilterInUrl}
              onSubcategoryFilterChange={setSubcategoryFilterInUrl}
              domainMin={domainMin}
              domainMax={domainMax}
              filterMin={filterMin}
              filterMax={filterMax}
              onFilterMinChange={handleFilterMinChange}
              onFilterMaxChange={handleFilterMaxChange}
              retailers={visibleRetailerNames}
              selectedRetailers={selectedRetailers}
              onToggleRetailer={toggleRetailer}
              onlyInStock={onlyInStock}
              onOnlyInStockChange={handleOnlyInStockChange}
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              saleActive={saleActive}
              onSaleChange={setSaleFilter}
            />
          </div>
        )}
        {!loadError && (totalCount > 0 || products.length > 0 || showSearchAlerts) && (
          <div className="results-page__toolbar results-page__content-width">
            <div className="results-page__toolbar-start">
              <p className="results-page__count">
                <span className="results-page__count-num">
                  {formatCount(totalCount)}
                </span>{' '}
                {totalCount === 1 ? 'product' : 'products'}
              </p>
              {showSearchAlerts && (
                <GetAlertsButton
                  onClick={openSearchAlertModal}
                  label={`Watch this search for ${searchAlertPayload.scopeLabel}`}
                />
              )}
              <ShareToolbarButton
                onClick={openSearchShareModal}
                label={
                  showSearchAlerts
                    ? `Share this search for ${searchAlertPayload.scopeLabel}`
                    : 'Share this page'
                }
              />
            </div>
            <div className="results-page__sort-row">
              <div className="results-page__sort">
                <label htmlFor="results-sort" className="sr-only">
                  Sort products
                </label>
                <span className="results-page__sort-icon" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M5 2.5L6.75 5.75H3.25L5 2.5zM4.25 5.75h1.5v6.5h-1.5V5.75z" />
                    <path d="M10.25 3.75h1.5v6.5h-1.5V3.75zM11 13.5l1.75-3.25H9.25L11 13.5z" />
                  </svg>
                </span>
                <select
                  id="results-sort"
                  className="results-page__select"
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  aria-label="Sort products"
                >
                  <option value="featured">Featured</option>
                  <option value="price-asc">Price - low to high</option>
                  <option value="price-desc">Price - high to low</option>
                  <option value="discount-desc">Discount - highest first</option>
                </select>
              </div>
              <FilterViewToggle
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                className="filter-view-toggle--in-toolbar"
              />
            </div>
          </div>
        )}
        {!loadError && effectiveQuery.trim() !== '' && !strictSearch && getSearchFallbackStepInfo(searchFallback?.searchMode) && (
          <div
            className={[
              'results-page__broad-notice',
              'results-page__content-width',
              appliedFallbackStep != null && appliedFallbackStep >= 4 && appliedFallbackStep <= 6
                ? 'results-page__broad-notice--step4'
                : '',
            ].filter(Boolean).join(' ')}
            role="status"
          >
            <div className="results-page__broad-notice-main">
              {totalCount > 0 && searchFallback?.searchMode === 'partialMatch' && (
                <SearchPartialMatchNotice
                  searchFallback={searchFallback}
                  browseCategories={browseCategories}
                  shopCategorySlug={shopCategorySlug}
                  onQuerySearch={applyFallbackQuerySearch}
                />
              )}
              {(() => {
                const fallbackNotice = getSearchFallbackNotice(
                  searchFallback,
                  browseCategories,
                  shopCategorySlug,
                );
                return fallbackNotice ?? null;
              })()}
            </div>
            {!embedded && searchFallback?.searchMode && !strictSearch && (
              <div className="results-page__broad-notice-actions">
                <button
                  type="button"
                  className="results-page__strict-search-link"
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    resetFiltersForRequery(next);
                    next.set('strict', '1');
                    setSearchParams(next);
                  }}
                >
                  Search exactly
                </button>
              </div>
            )}
          </div>
        )}
        {!loadError && !loading && searchFallbackOffer?.searchMode && (
          <div
            className="results-page__fallback-offer results-page__content-width"
            role="region"
            aria-label="Broader search available"
          >
            <p className="results-page__fallback-offer-text">
              {getSearchFallbackOfferNotice(searchFallbackOffer)}
            </p>
            <div className="results-page__fallback-offer-actions">
              <button
                type="button"
                className="results-page__fallback-offer-confirm"
                onClick={confirmSevereFallback}
              >
                Show broader results
              </button>
            </div>
          </div>
        )}
        <section
          className="results-page__grid-wrap results-page__content-width"
          aria-live="polite"
        >
          {loading && (
            <ProductGridSkeleton
              count={PRODUCTS_PER_PAGE}
              layout={viewMode === 'list' ? 'list' : 'grid'}
              gridClassName={
                viewMode === 'list'
                  ? 'results-page__grid results-page__grid--list'
                  : 'results-page__grid'
              }
              cellClassName="results-page__cell"
            />
          )}
          {!loading && loadError !== null && (
            <p className="results-page__hint results-page__hint--error" role="alert">
              {loadError}
            </p>
          )}
          {!loading && loadError === null && totalCount === 0 && (
            <>
              <p className="results-page__hint">
                {hasActiveFilters
                  ? (
                    SEARCH_RETAILER_FILTER_ENABLED
                      ? 'No products match your filters. Try adjusting price, retailer, or in-stock filter.'
                      : 'No products match your filters. Try adjusting price or in-stock filter.'
                  )
                  : effectiveQuery.trim()
                    ? saleActive
                      ? 'No sale items match your search.'
                      : 'No products match your search.'
                    : activeWebsiteHost
                      ? 'No products from this store yet.'
                      : shopCategorySlug
                        ? 'No products in this category yet.'
                        : productCategoryId
                          ? 'No products in this category.'
                          : saleActive
                            ? 'No sale items match your filters yet.'
                            : 'No products yet.'}
              </p>
              {showSearchAlerts && (
                <SearchAlertSignupPanel
                  variant="empty"
                  scopeLabel={searchAlertPayload.scopeLabel}
                  scopeChips={searchAlertPayload.scopeChips}
                  onShare={() => openPanelShareModal('empty')}
                  onWatch={openSearchAlertModal}
                />
              )}
            </>
          )}
          {!loading && loadError === null && products.length > 0 && (
            <ul
              className={
                viewMode === 'list'
                  ? 'results-page__grid results-page__grid--list'
                  : 'results-page__grid'
              }
            >
              {products.map((product, index) => {
                const pageNum = Math.floor(index / PRODUCTS_PER_PAGE) + 1;
                const showPageSeparator =
                  index > 0 && index % PRODUCTS_PER_PAGE === 0;
                const showBrowseFacebookAtIndex =
                  showBrowseFacebookInline
                  && shouldShowBrowseFacebookInlineAtIndex(
                    index,
                    PRODUCTS_PER_PAGE,
                  );

                return (
                  <Fragment key={product.id}>
                    {showPageSeparator && (
                      <li
                        className="results-page__page-separator"
                        role="separator"
                        aria-label={`Page ${pageNum}`}
                      >
                        <span className="results-page__page-separator-label">
                          Page {pageNum}
                        </span>
                      </li>
                    )}
                    <li className="results-page__cell">
                      <ProductCard
                        product={product}
                        layout={viewMode === 'list' ? 'list' : 'grid'}
                        outboundCampaign={outboundCampaign}
                        enableReturnCta
                        analyticsContext={{
                          query: effectiveQuery,
                          shopCategorySlug: shopCategorySlug || null,
                          source: outboundCampaign,
                          page: pageNum,
                          position: index + 1,
                          searchFallbackStep: searchAnalyticsStep,
                        }}
                      />
                    </li>
                    {showBrowseFacebookAtIndex && (
                      <SubcategoryBrowseFacebookInline
                        scopeLabel={embeddedBrowseScopeLabel}
                        location={browseFacebookLocation}
                        onShare={openBrowseInlineShareModal}
                      />
                    )}
                  </Fragment>
                );
              })}
            </ul>
          )}
          {!loading && loadError === null && products.length > 0 && (
            <div
              ref={loadMoreSentinelRef}
              className="results-page__load-more"
              aria-hidden={!hasMore && !loadingMore && loadMoreError === null}
            >
              {loadingMore && (
                <ProductGridSkeleton
                  count={LOAD_MORE_SKELETON_COUNT}
                  layout={viewMode === 'list' ? 'list' : 'grid'}
                  gridClassName={
                    viewMode === 'list'
                      ? 'results-page__grid results-page__grid--list results-page__grid--load-more'
                      : 'results-page__grid results-page__grid--load-more'
                  }
                  cellClassName="results-page__cell"
                />
              )}
              {loadMoreError !== null && (
                <div className="results-page__load-more-error">
                  <p role="alert">{loadMoreError}</p>
                  <button
                    type="button"
                    className="results-page__load-more-retry"
                    onClick={retryLoadMore}
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
          )}
          {!loading
            && loadError === null
            && products.length > 0
            && !hasMore
            && !loadingMore
            && showSearchAlerts && (
            <SearchAlertSignupPanel
              variant="end"
              scopeLabel={searchAlertPayload.scopeLabel}
              scopeChips={searchAlertPayload.scopeChips}
              onShare={() => openPanelShareModal('end')}
              onWatch={openSearchAlertModal}
            />
          )}
        </section>
      </div>
  );

  const searchAlertModal = searchAlertOpen && showSearchAlerts && (
    <SearchAlertSignupModal
      onClose={() => setSearchAlertOpen(false)}
      searchPath={searchAlertPayload.searchPath}
      scopeLabel={searchAlertPayload.scopeLabel}
      scopeChips={searchAlertPayload.scopeChips}
    />
  );

  if (embedded) {
    return (
      <>
        {resultsMain}
        {searchAlertModal}
      </>
    );
  }

  return (
    <div className="results-page">
      <PageHead
        title="Search results | Shops.mt"
        robots="noindex, follow"
      />
      <h1 className="sr-only">Search results</h1>
      <header className="results-page__header">
        <div className="results-page__header-inner">
          <div className="results-page__header-left">
            <LogoMark variant="header" />
            <CategoryMenuDropdown
              browseCategories={browseCategories}
              activeShopCategorySlug={shopCategorySlug}
              disabled={loading}
            />
            <HeaderMoreMenu />
          </div>
          <div className="results-page__header-center">
            <SearchField
              id="results-search"
              className="results-page__search"
              value={draftQuery}
              onChange={setDraftQuery}
              onSubmitSearch={applySearch}
              searchSubmitScope={searchSubmitScope}
              placeholder="Search products…"
              gaSearchLocation="results_header"
              scopePill={
                searchScopePill
                  ? { ...searchScopePill, onRemove: dismissCategoryScopePill }
                  : null
              }
            />
          </div>
          <nav
            className="results-page__header-nav"
            aria-label="Site sections"
          >
            <HeaderShareButton location="header" />
            <Link className="results-page__nav-link" to="/about">
              About
            </Link>
            <Link className="results-page__nav-link" to="/add-store">
              Add your store
            </Link>
          </nav>
        </div>
      </header>
      {resultsMain}
      <SiteFooter
        browseCategories={browseCategories}
        activeShopCategorySlug={scopePillDismissed ? '' : shopCategorySlug}
      />
      {searchAlertModal}
    </div>
  );
}

